import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
  isMobileBreakpoint,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails element.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());
  #activeIndex = 0;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    window.addEventListener('resize', this.#handleResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.#handleResize);
  }

  #handleResize = debounce(() => {
    if (!this.refs.dialog.open) return;
    this.#activateMedia(this.#activeIndex, { behavior: 'instant' });
  }, 50);

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media } = this.refs;
    const targetImage = media[index];

    const open = () => {
  dialog.showModal();

  requestAnimationFrame(() => {
    dialog.focus({ preventScroll: true });
  });

  this.#activateMedia(index, { behavior: 'instant' });
};

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) {
      open();
      return;
    }

    const itemTransitionName = 'gallery-item-open';
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');
  }

  /**
   * Loads a high-resolution image for a specific media container.
   *
   * @param {HTMLElement} mediaContainer - The media container element.
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Mobile-only scroll tracking for the native horizontal gallery.
   */
  handleScroll = debounce(async () => {
    if (!isMobileBreakpoint()) return;

    const { media, thumbnails } = this.refs;
    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    this.#activeIndex = activeIndex;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;
    const activeMedia = isMobileBreakpoint()
      ? await getMostVisibleElement(media)
      : media[this.#activeIndex];

    if (!activeMedia) return this.closeDialog();

    this.#resetAllZoom();

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    const activeIndex = media.indexOf(activeMedia);
    const itemTransitionName = 'gallery-item-close';

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';
    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();

    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    if (isMobileBreakpoint()) {
      await onAnimationEnd(this.refs.thumbnails);
    }

    activeMedia.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      activeMedia.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

 closeDialog() {
  const { dialog } = this.refs;

  this.#resetAllZoom();

  /* Update the main product gallery only when desktop zoom closes */
  if (!isMobileBreakpoint()) {
    this.dispatchEvent(new ZoomMediaSelectedEvent(this.#activeIndex));
  }

  dialog.close();
  window.dispatchEvent(new DialogCloseEvent());
}

  /**
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (isMobileBreakpoint()) return;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previous();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    }
  }

  /**
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * @param {number} index - The index of the thumbnail to preload.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - Selection options.
   * @param {ScrollBehavior} options.behavior - Scroll behavior.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    this.#activateMedia(index, options);
  }

  /**
   * Shows the previous image on desktop.
   *
   * @param {Event} [event]
   */
  previous(event) {
    event?.preventDefault();
    const count = this.refs.media.length;
    if (count < 2) return;

    const previousIndex = (this.#activeIndex - 1 + count) % count;
    this.#activateMedia(previousIndex, { behavior: 'instant' });
  }

  /**
   * Shows the next image on desktop.
   *
   * @param {Event} [event]
   */
  next(event) {
    event?.preventDefault();
    const count = this.refs.media.length;
    if (count < 2) return;

    const nextIndex = (this.#activeIndex + 1) % count;
    this.#activateMedia(nextIndex, { behavior: 'instant' });
  }

  /**
   * Activates one image on desktop while preserving the native mobile gallery.
   *
   * @param {number} index
   * @param {{ behavior: ScrollBehavior }} options
   */
  #activateMedia(index, options = { behavior: 'instant' }) {
    const { media, thumbnails } = this.refs;

    if (isNaN(index) || index < 0 || index >= media.length) return;

    const targetMedia = media[index];
    if (!targetMedia) return;

    this.#activeIndex = index;
    this.#resetAllZoom();

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    const targetThumbnail = thumbnails.children[index];
    if (targetThumbnail instanceof HTMLElement) {
      scrollIntoView(targetThumbnail, {
        ancestor: thumbnails,
        behavior: options.behavior,
        block: 'center',
        inline: 'center',
      });
    }

    if (isMobileBreakpoint()) {
      for (const item of media) {
        item.hidden = false;
        item.removeAttribute('aria-hidden');
      }

      targetMedia.scrollIntoView({ behavior: options.behavior });
    } else {
      media.forEach((item, itemIndex) => {
        const isActive = itemIndex === index;
        item.hidden = !isActive;
        item.setAttribute('aria-hidden', `${!isActive}`);
      });
    }
      this.loadHighResolutionImage(targetMedia);

    /* Keep the native live synchronization on mobile only */
      if (isMobileBreakpoint()) {
      this.dispatchEvent(new ZoomMediaSelectedEvent(index));
}
  }

  #resetAllZoom() {
    const wrappers = this.querySelectorAll('drag-zoom-wrapper');

    for (const wrapper of wrappers) {
      if ('resetZoom' in wrapper && typeof wrapper.resetZoom === 'function') {
        wrapper.resetZoom();
      }
    }
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * @param {HTMLElement[]} elements - Elements to compare.
 * @returns {Promise<HTMLElement>}
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
